<script lang="ts">
import Modal from './Modal.vue'
import Date from './Date.vue'
import { computed, defineComponent, PropType } from 'vue'
import { User } from '../../../libs/models'
import illustUrl from '../libs/illustUrl'
import UserTextContent from './UserTextContent.vue'
import { useCurrentUserStore } from '../libs/currentUserStore'
import { useFollowsStore } from '../libs/followsStore'
export default defineComponent({
  emits: ['close'],
  components: { Modal, Date, UserTextContent },
  props: {
    user: { required: true, type: Object as PropType<User> }
  },
  setup (props) {
    const currentUserStore = useCurrentUserStore()
    const followsStore = useFollowsStore()
    const canBookmark = computed(() => !!currentUserStore.value && currentUserStore.value.id !== props.user.id)
    const isBookmarked = computed(() => followsStore.getIsFollowed(props.user.id))
    const icon = computed(() => props.user.illust_id ? illustUrl(props.user.id, props.user.illust_id) : null)
    const cover = computed(() => props.user.cover_illust_id ? illustUrl(props.user.id, props.user.cover_illust_id) : null)
    return {
      icon, cover,
      canBookmark, isBookmarked, followsStore
    }
  }
})
</script>

<template>
  <Modal title="" @close="$emit('close')">
    <div class="UserInfoCover" :style="{ backgroundImage: cover ? `url(${cover})` : undefined }" />
    <div class="ModalContent">
      <div class="UserInfoContent">
        <div class="UserInfoContent-icon" v-if="icon">
          <img :src="icon" alt="">
        </div>
        <div class="UserInfoContent-info">
          <div class="UserInfoContent-info-name" :class="{ 'has-actions': canBookmark }">
            {{ user.name }}
          </div>
          <div class="UserInfoContent-info-detail">
            {{ l('ユーザーID') }}: <a :href="`/users/${user.id}`" target="_blank">{{ user.id }}</a><br>
            {{ l('登録日') }}: <Date :datetime="user.created" date />
            <span v-if="user.registered">（{{ l('Googleログイン') }}）</span>
          </div>
        </div>
        <div class="UserInfoContent-actions" v-if="canBookmark">
          <button
            class="ProfileBookmark"
            :class="{ active: isBookmarked }"
            :aria-pressed="isBookmarked"
            :aria-label="isBookmarked ? l('ブックマークから削除') : l('ブックマーク')"
            :title="isBookmarked ? l('ブックマークから削除') : l('ブックマーク')"
            @click="isBookmarked ? followsStore.unfollow(user.id) : followsStore.follow(user.id)"
          >
            <span class="material-symbols-rounded" aria-hidden="true">star</span>
          </button>
        </div>
        <UserTextContent class="UserInfoContent-desc" v-if="user.desc" :text="user.desc" is="div" @tagClick="$emit('close')" />
      </div>
      <slot />
    </div>
  </Modal>
</template>

<style lang="scss" scoped>
.ModalContent {
  width: 400px;
}
.ProfileBookmark {
  display: grid;
  place-items: center;
  box-sizing: border-box;
  width: 36px;
  height: 36px;
  margin: 0;
  padding: 0;
  border-radius: 50%;
  border: 1px solid var(--tx-color-light);
  background: var(--bg-color);
  color: var(--tx-color-light);
  text-shadow: none;
  .material-symbols-rounded {
    grid-area: 1 / 1;
    margin: 0;
    font-size: 24px;
    font-variation-settings: 'FILL' 0;
  }
  &.active {
  border-color: var(--orange);
    background: var(--orange);
    color: #fff;
    .material-symbols-rounded {
      font-variation-settings: 'FILL' 1;
    }
  }
}
.UserInfoCover {
  height: 120px;
  background-color: rgba(255,255,255,0.5);
  background-size: cover;
  background-position: center;
  border-radius: var(--radius) var(--radius) 0 0;
  box-shadow: inset 0 -10px 15px rgba(0, 0, 0, 0.2);
}
.UserInfoContent {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  margin-top: -50px;
  margin-bottom: 12px;
  line-height: 1.5;
  &-icon {
    margin-right: 15px;
    width: 92px;
    height: 92px;
    overflow: hidden;
    border-radius: 100px;
    border: 3px solid var(--bg-color);
    background: var(--bg-color);
    img {
      display: block;
      object-fit: cover;
      max-width: none;
      width: 100%;
      height: 100%;
    }
  }
  &-info {
    flex: 1;
    padding-top: 26px;
    font-size: 13px;
    @include sp {
      font-size: 12px;
    }
    text-shadow: 1px 1px 0 var(--bg-color), -1px 1px 0 var(--bg-color), 1px -1px 0 var(--bg-color), -1px -1px 0 var(--bg-color), 0px 1px 0 var(--bg-color), 0px -1px 0 var(--bg-color), 1px 0px 0 var(--bg-color), -1px 0px 0 var(--bg-color);
    &-name {
      font-weight: bold;
      font-size: 17px;
      overflow-wrap: anywhere;
      &.has-actions {
        padding-right: 44px;
      }
    }
    &-detail {
      margin-top: 3px;
      a {
        font-weight: bold;
        color: var(--green);
      }
    }
  }
  &-actions {
    position: absolute;
    top: 20px;
    right: 0;
  }
  &-desc {
    flex-basis: 100%;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px dashed var(--border-color);
    white-space: pre-wrap;
    max-height: 20em;
    overflow-y: scroll;
  }
}
</style>
